export const POPOVER_DESKTOP_MIN_WIDTH = 1200;

/** Direct-child option cards that carry popover HTML. Independent of `data-bs-toggle`, which is blank on mobile. */
export const OWNED_POPOVER_TRIGGER_SELECTOR = ':scope > [data-bs-content]';

export function isDesktopPopoverEnabled(width: number): boolean {
    return width > POPOVER_DESKTOP_MIN_WIDTH;
}

export type PopoverInstance = {
    dispose: () => void;
    hide?: () => void;
};

export type PopoverTrigger = {
    isConnected?: boolean;
    addEventListener: (type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) => void;
    removeEventListener: (type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions) => void;
    getAttribute?: (qualifiedName: string) => string | null;
    setAttribute?: (qualifiedName: string, value: string) => void;
};

export type PopoverQueryRoot = {
    querySelectorAll: (selectors: string) => ArrayLike<PopoverTrigger>;
};

export type PopoverFactory = {
    getOrCreateInstance?: (element: PopoverTrigger, config?: unknown) => PopoverInstance;
    getInstance?: (element: PopoverTrigger) => PopoverInstance | null | undefined;
    create?: (element: PopoverTrigger, config?: unknown) => PopoverInstance;
    defaultAllowList?: Record<string, unknown[]>;
};

export type OwnedPopoverSyncInput = {
    container: PopoverQueryRoot | null | undefined;
    enabled: boolean;
};

export type OwnedPopoverController = {
    sync: (input: OwnedPopoverSyncInput) => void;
    disposeAll: () => void;
    ownedTriggers: () => PopoverTrigger[];
};

export type OwnedPopoverControllerOptions = {
    popover?: PopoverFactory | (() => PopoverFactory | undefined);
    queryTriggers?: (container: PopoverQueryRoot) => PopoverTrigger[];
    createConfig?: (element: PopoverTrigger) => unknown;
};

type OwnedRecord = {
    instance: PopoverInstance;
    onClick: EventListener;
    content: string | null;
};

/**
 * Copy Bootstrap's allow list and add `style` on a new `*` array.
 * Never mutates the object Bootstrap owns, so repeated owner mounts cannot grow Default.allowList.
 */
export function copyAllowListWithStyle(defaultAllowList: Record<string, unknown[]> | null | undefined): Record<string, unknown[]> | undefined {
    if (!defaultAllowList) {
        return undefined;
    }

    const star = Array.isArray(defaultAllowList['*']) ? [...defaultAllowList['*']] : [];
    if (!star.includes('style')) {
        star.push('style');
    }

    return {
        ...defaultAllowList,
        '*': star,
    };
}

export function getBootstrapPopoverApi(): PopoverFactory | undefined {
    const fromGlobal = (globalThis as {bootstrap?: {Popover?: any}}).bootstrap?.Popover;
    const fromWindow = typeof window !== 'undefined' ? (window as {bootstrap?: {Popover?: any}}).bootstrap?.Popover : undefined;
    const Popover = fromGlobal ?? fromWindow;
    if (!Popover) {
        return undefined;
    }

    return {
        getOrCreateInstance: typeof Popover.getOrCreateInstance === 'function' ? (el, config) => Popover.getOrCreateInstance(el, config) : undefined,
        getInstance: typeof Popover.getInstance === 'function' ? el => Popover.getInstance(el) : undefined,
        create: (el, config) => new Popover(el, config),
        defaultAllowList: Popover.Default?.allowList,
    };
}

function defaultQueryTriggers(container: PopoverQueryRoot): PopoverTrigger[] {
    return Array.from(container.querySelectorAll(OWNED_POPOVER_TRIGGER_SELECTOR));
}

function resolvePopoverFactory(popover: OwnedPopoverControllerOptions['popover']): PopoverFactory | undefined {
    if (!popover) {
        return getBootstrapPopoverApi();
    }
    return typeof popover === 'function' ? popover() : popover;
}

function getOrCreatePopover(api: PopoverFactory, element: PopoverTrigger, config: unknown): PopoverInstance | undefined {
    if (typeof api.getOrCreateInstance === 'function') {
        return api.getOrCreateInstance(element, config);
    }

    const existing = api.getInstance?.(element);
    if (existing) {
        return existing;
    }

    return api.create?.(element, config);
}

function setPopoverToggle(element: PopoverTrigger, enabled: boolean): void {
    if (typeof element.setAttribute !== 'function') {
        return;
    }
    element.setAttribute('data-bs-toggle', enabled ? 'popover' : '');
}

function instanceConfig(element: PopoverTrigger, createConfig: OwnedPopoverControllerOptions['createConfig']): Record<string, unknown> {
    const userConfig = createConfig?.(element);
    // Bootstrap 5.2.3 hide() fade callbacks crash if dispose() runs first (twbs/bootstrap#37474).
    if (userConfig && typeof userConfig === 'object') {
        return {...(userConfig as Record<string, unknown>), animation: false};
    }
    return {animation: false};
}

function teardown(element: PopoverTrigger, record: OwnedRecord): void {
    element.removeEventListener('click', record.onClick);
    try {
        record.instance.hide?.();
    } finally {
        record.instance.dispose();
    }
}

function getTriggerContent(element: PopoverTrigger): string | null {
    return typeof element.getAttribute === 'function' ? element.getAttribute('data-bs-content') : null;
}

export function createOwnedPopoverController(options: OwnedPopoverControllerOptions = {}): OwnedPopoverController {
    const owned = new Map<PopoverTrigger, OwnedRecord>();
    const queryTriggers = options.queryTriggers ?? defaultQueryTriggers;

    const disposeAll = () => {
        owned.forEach((record, element) => {
            teardown(element, record);
        });
        owned.clear();
    };

    const sync = (input: OwnedPopoverSyncInput) => {
        if (!input.enabled || !input.container) {
            owned.forEach((_record, element) => {
                setPopoverToggle(element, false);
            });
            disposeAll();
            return;
        }

        const api = resolvePopoverFactory(options.popover);
        if (!api) {
            return;
        }

        const current = queryTriggers(input.container);
        const currentSet = new Set(current);

        owned.forEach((record, element) => {
            if (!currentSet.has(element)) {
                teardown(element, record);
                owned.delete(element);
            }
        });

        current.forEach(element => {
            setPopoverToggle(element, true);

            const content = getTriggerContent(element);
            const existing = owned.get(element);
            if (existing) {
                if (existing.content === content) {
                    return;
                }
                teardown(element, existing);
                owned.delete(element);
            }

            const config = instanceConfig(element, options.createConfig);
            const instance = getOrCreatePopover(api, element, config);
            if (!instance) {
                return;
            }

            const onClick: EventListener = () => {
                // Data-api click also toggle()s this trigger. Hide after that so toggle cannot show it again.
                queueMicrotask(() => {
                    const current = owned.get(element);
                    if (current?.onClick !== onClick) {
                        return;
                    }
                    current.instance.hide?.();
                });
            };
            element.addEventListener('click', onClick);
            owned.set(element, {instance, onClick, content});
        });
    };

    return {
        sync,
        disposeAll,
        ownedTriggers: () => [...owned.keys()],
    };
}
